import {
  STAFF_CATALOG_BOUNDS,
  staffCatalogCreateCategoryBodySchema,
  staffCatalogCreatePersonBodySchema,
  staffCatalogCreateTagBodySchema,
  type staffCatalogTagNamespaces,
} from "@lafayette/schemas";
import { useMemo, useState } from "react";
import { NameWord, opaqueKey } from "../../ui/accessible-name";
import { Button } from "../../ui/button";
import { SelectField } from "../../ui/fields";
import { RadioInput, RadioRow } from "../../ui/radio";
import { SectionCard } from "../../ui/section-card";
import { SectionForm } from "../../ui/section-form";
import { describeOutcome } from "../commands";
import { CreateEntityDialog, type EntityField } from "../components/CreateEntityDialog";
import { LookupAdd } from "../components/LookupAdd";
import { LiveRegion, MoveButtons, useAnnouncer, useMoveFocus } from "../components/reorder";
import {
  chosenName,
  chosenRow,
  groupHeading,
  hintText,
  plainList,
  subheading,
  subsection,
} from "../components/styles";
import {
  addCategory,
  addPerson,
  addTag,
  type CategoryDraft,
  categoriesToBody,
  categoryDraftsFromDetail,
  moveItem,
  type PersonDraft,
  peopleToBody,
  personDraftsFromDetail,
  removeCategory,
  removeTag,
  setPrimaryCategory,
  type TagDraft,
  tagDraftsFromDetail,
  tagsToBody,
} from "../form-mappers";
import {
  personRoleLabels,
  personRoleOptions,
  tagNamespaceLabels,
  tagNamespaceOptions,
} from "../labels";
import { useCatalogRuntime } from "../runtime";
import { describeIssue, type FieldErrors, type SchemaIssue } from "../validation";
import { type ItemSectionContext, useSectionState } from "./section-support";

type StaffCatalogTagNamespace = (typeof staffCatalogTagNamespaces)[number];

const NO_FIELDS: readonly { id: string; label: string }[] = [];

function firstErrors(
  issues: readonly SchemaIssue[],
  names: Readonly<Record<string, string>>,
): FieldErrors {
  const errors: Record<string, string> = {};
  for (const issue of issues) {
    const key = typeof issue.path[0] === "string" ? issue.path[0] : "";
    if (names[key] !== undefined && errors[key] === undefined) errors[key] = describeIssue(issue);
  }
  return errors;
}

/** Categories, tags and people. Each list saves on its own with a replace-set command. */
export function TaxonomySection({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  return (
    <SectionCard
      id="taxonomy"
      title="Categories, tags and people"
      description="How the item is found and who is connected to it. Each list is saved on its own."
    >
      <CategoriesForm ctx={ctx} />
      <TagsForm ctx={ctx} />
      <PeopleForm ctx={ctx} />
    </SectionCard>
  );
}

// --- categories -----------------------------------------------------------------------------------------

const categoryFields: readonly EntityField[] = [
  {
    key: "code",
    label: "Code",
    kind: "text",
    required: true,
    hint: "Lowercase letters, numbers and underscores, for example fine_dining.",
  },
  { key: "name", label: "Name", kind: "text", required: true },
  {
    key: "parentCode",
    label: "Parent code",
    kind: "text",
    hint: "Optional. The code of an existing category.",
  },
];

function CategoriesForm({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command } = ctx;
  const { api } = useCatalogRuntime();
  const server = useMemo(() => categoryDraftsFromDetail(detail), [detail]);
  const section = useSectionState<readonly CategoryDraft[]>("categories", server);
  const list = section.values;
  const [creating, setCreating] = useState(false);
  const live = useAnnouncer();

  async function submit() {
    if (list.length > STAFF_CATALOG_BOUNDS.categories) {
      section.setMessage({
        tone: "error",
        text: `Choose at most ${STAFF_CATALOG_BOUNDS.categories} categories.`,
      });
      return;
    }
    section.clearErrors();
    const outcome = await command.run("replaceCategories", {
      expectedVersion: detail.item.version,
      categories: categoriesToBody(list),
    });
    section.setMessage(describeOutcome(outcome, "Categories saved."));
    if (outcome.ok) section.reset(categoryDraftsFromDetail(outcome.detail));
  }

  return (
    <section className={subsection} aria-labelledby="categories-title">
      <h3 id="categories-title" className={subheading}>
        Categories
      </h3>
      <SectionForm
        label="Categories"
        dirty={section.dirty}
        pending={command.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={NO_FIELDS}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
      >
        {list.length === 0 ? (
          <p className={hintText}>
            No category yet. The first one you add is the primary category.
          </p>
        ) : (
          <ul className={plainList}>
            {list.map((entry, index) => (
              <li key={entry.code} className={chosenRow}>
                <span id={`category-name-${index}`} className={chosenName}>
                  {entry.name}
                </span>
                <NameWord id={`category-primary-${index}`}>Primary:</NameWord>
                <NameWord id={`category-remove-${index}`}>Remove category</NameWord>
                <RadioRow>
                  <RadioInput
                    name="primary-category"
                    aria-labelledby={`category-primary-${index} category-name-${index}`}
                    checked={entry.isPrimary}
                    disabled={readOnly}
                    onChange={() => {
                      section.setValues((current) => setPrimaryCategory(current, entry.code));
                      live.announce(`${entry.name} is now the primary category.`);
                    }}
                  />
                  Primary
                </RadioRow>
                {readOnly ? null : (
                  <Button
                    type="button"
                    variant="secondary"
                    aria-labelledby={`category-remove-${index} category-name-${index}`}
                    onClick={() => {
                      section.setValues((current) => removeCategory(current, entry.code));
                      live.announce(`Removed category ${entry.name}.`);
                    }}
                  >
                    Remove
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {readOnly ? null : (
          <LookupAdd
            label="Find a category"
            search={async (query, signal) =>
              (
                await api.searchCategories(
                  { limit: 10, ...(query === "" ? {} : { q: query }) },
                  { signal },
                )
              ).items
            }
            getKey={(item) => item.code}
            getLabel={(item) => item.name}
            describe={(item) => item.code}
            isChosen={(item) => list.some((entry) => entry.code === item.code)}
            onPick={(item) =>
              section.setValues((current) =>
                addCategory(current, { code: item.code, name: item.name }),
              )
            }
            disabled={command.pending}
            onCreate={() => setCreating(true)}
            createLabel="Create category"
          />
        )}
        <LiveRegion message={live.message} />
      </SectionForm>
      {creating ? (
        <CreateEntityDialog
          title="Create a category"
          fields={categoryFields}
          initial={{ code: "", name: "", parentCode: "" }}
          build={(values) => {
            const body = {
              code: (values.code ?? "").trim(),
              name: (values.name ?? "").trim(),
              ...((values.parentCode ?? "").trim() === ""
                ? {}
                : { parentCode: (values.parentCode ?? "").trim() }),
            };
            const checked = staffCatalogCreateCategoryBodySchema.safeParse(body);
            return checked.success
              ? { ok: true, body }
              : {
                  ok: false,
                  errors: firstErrors(checked.error.issues, { code: "", name: "", parentCode: "" }),
                };
          }}
          submit={async (body, idempotencyKey) =>
            void (await api.createCategory(body, { idempotencyKey }))
          }
          onCreated={(body) =>
            section.setValues((current) =>
              addCategory(current, { code: body.code, name: body.name }),
            )
          }
          onClose={() => setCreating(false)}
        />
      ) : null}
    </section>
  );
}

// --- tags -------------------------------------------------------------------------------------------------

const tagFields: readonly EntityField[] = [
  {
    key: "namespace",
    label: "Kind of tag",
    kind: "select",
    options: tagNamespaceOptions,
    required: true,
  },
  {
    key: "code",
    label: "Code",
    kind: "text",
    required: true,
    hint: "Lowercase letters, numbers and underscores, for example rooftop.",
  },
  { key: "label", label: "Label", kind: "text", required: true },
];

function TagsForm({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command } = ctx;
  const { api } = useCatalogRuntime();
  const server = useMemo(() => tagDraftsFromDetail(detail), [detail]);
  const section = useSectionState<readonly TagDraft[]>("tags", server);
  const list = section.values;
  const [namespace, setNamespace] = useState("");
  const [creating, setCreating] = useState(false);
  const live = useAnnouncer();

  async function submit() {
    if (list.length > STAFF_CATALOG_BOUNDS.tags) {
      section.setMessage({
        tone: "error",
        text: `Choose at most ${STAFF_CATALOG_BOUNDS.tags} tags.`,
      });
      return;
    }
    section.clearErrors();
    const outcome = await command.run("replaceTags", {
      expectedVersion: detail.item.version,
      tags: tagsToBody(list),
    });
    section.setMessage(describeOutcome(outcome, "Tags saved."));
    if (outcome.ok) section.reset(tagDraftsFromDetail(outcome.detail));
  }

  const groups = tagNamespaceOptions
    .map((option) => ({ ...option, tags: list.filter((tag) => tag.namespace === option.value) }))
    .filter((group) => group.tags.length > 0);

  return (
    <section className={subsection} aria-labelledby="tags-title">
      <h3 id="tags-title" className={subheading}>
        Tags
      </h3>
      <SectionForm
        label="Tags"
        dirty={section.dirty}
        pending={command.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={NO_FIELDS}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
      >
        {groups.length === 0 ? <p className={hintText}>No tags yet.</p> : null}
        {groups.map((group, groupIndex) => (
          <div key={group.value} className="grid gap-2">
            <h4 className={groupHeading}>{group.label}</h4>
            <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
              {group.tags.map((tag, tagIndex) => {
                const at = `${groupIndex}-${tagIndex}`;
                return (
                  <li
                    key={`${tag.namespace}:${tag.code}`}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-neutral-soft pl-3 text-sm text-neutral-foreground"
                  >
                    <span id={`tag-name-${at}`}>{tag.label}</span>
                    <NameWord id={`tag-remove-${at}`}>Remove tag</NameWord>
                    <NameWord id={`tag-group-${at}`}>({group.label})</NameWord>
                    {readOnly ? null : (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="rounded-full"
                        aria-labelledby={`tag-remove-${at} tag-name-${at} tag-group-${at}`}
                        onClick={() => {
                          section.setValues((current) => removeTag(current, tag));
                          live.announce(`Removed tag ${tag.label}.`);
                        }}
                      >
                        <span aria-hidden="true">×</span>
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {readOnly ? null : (
          <LookupAdd
            label="Find a tag"
            before={
              <SelectField
                id="tags-namespace"
                label="Kind of tag"
                value={namespace}
                onChange={setNamespace}
                options={tagNamespaceOptions}
                placeholder="Any kind"
              />
            }
            searchKey={namespace}
            search={async (query, signal) =>
              (
                await api.searchTags(
                  {
                    limit: 10,
                    ...(query === "" ? {} : { q: query }),
                    ...(namespace === ""
                      ? {}
                      : { namespace: namespace as StaffCatalogTagNamespace }),
                  },
                  { signal },
                )
              ).items
            }
            getKey={(item) => `${item.namespace}:${item.code}`}
            getLabel={(item) => item.label}
            describe={(item) => tagNamespaceLabels[item.namespace]}
            isChosen={(item) =>
              list.some((tag) => tag.namespace === item.namespace && tag.code === item.code)
            }
            onPick={(item) => section.setValues((current) => addTag(current, item))}
            disabled={command.pending}
            onCreate={() => setCreating(true)}
            createLabel="Create tag"
          />
        )}
        <LiveRegion message={live.message} />
      </SectionForm>
      {creating ? (
        <CreateEntityDialog
          title="Create a tag"
          fields={tagFields}
          initial={{ namespace: namespace === "" ? "GENERAL" : namespace, code: "", label: "" }}
          build={(values) => {
            const body = {
              namespace: values.namespace ?? "",
              code: (values.code ?? "").trim(),
              label: (values.label ?? "").trim(),
            };
            const checked = staffCatalogCreateTagBodySchema.safeParse(body);
            return checked.success
              ? { ok: true, body: checked.data }
              : {
                  ok: false,
                  errors: firstErrors(checked.error.issues, { namespace: "", code: "", label: "" }),
                };
          }}
          submit={async (body, idempotencyKey) =>
            void (await api.createTag(body, { idempotencyKey }))
          }
          onCreated={(body) =>
            section.setValues((current) =>
              addTag(current, { namespace: body.namespace, code: body.code, label: body.label }),
            )
          }
          onClose={() => setCreating(false)}
        />
      ) : null}
    </section>
  );
}

// --- people -----------------------------------------------------------------------------------------------

const personFields: readonly EntityField[] = [
  {
    key: "slug",
    label: "Slug",
    kind: "text",
    required: true,
    hint: "Lowercase letters, numbers and single hyphens, for example ana-ruiz.",
  },
  { key: "displayName", label: "Display name", kind: "text", required: true },
  {
    key: "personKind",
    label: "Kind",
    kind: "select",
    required: true,
    options: [
      { value: "INDIVIDUAL", label: "Individual" },
      { value: "GROUP", label: "Group" },
    ],
  },
  { key: "shortBio", label: "Short bio", kind: "textarea" },
];

function PeopleForm({ ctx }: Readonly<{ ctx: ItemSectionContext }>) {
  const { detail, readOnly, command } = ctx;
  const { api } = useCatalogRuntime();
  const server = useMemo(() => personDraftsFromDetail(detail), [detail]);
  const section = useSectionState<readonly PersonDraft[]>("people", server);
  const list = section.values;
  const [creating, setCreating] = useState(false);
  const live = useAnnouncer();
  const focus = useMoveFocus<HTMLOListElement>();

  async function submit() {
    if (list.length > STAFF_CATALOG_BOUNDS.people) {
      section.setMessage({
        tone: "error",
        text: `Choose at most ${STAFF_CATALOG_BOUNDS.people} people.`,
      });
      return;
    }
    section.clearErrors();
    const outcome = await command.run("replacePeople", {
      expectedVersion: detail.item.version,
      people: peopleToBody(list),
    });
    section.setMessage(describeOutcome(outcome, "People saved."));
    if (outcome.ok) section.reset(personDraftsFromDetail(outcome.detail));
  }

  function move(index: number, delta: -1 | 1) {
    const entry = list[index];
    if (entry === undefined) return;
    const rowKey = opaqueKey(`${entry.slug}:${entry.role}`);
    focus.requestFocus(rowKey, delta === -1 ? "up" : "down");
    section.setValues((current) => moveItem(current, index, delta));
    live.announce(`Moved ${entry.displayName} to position ${index + delta + 1} of ${list.length}.`);
  }

  return (
    <section className={subsection} aria-labelledby="people-title">
      <h3 id="people-title" className={subheading}>
        People
      </h3>
      <SectionForm
        label="People"
        dirty={section.dirty}
        pending={command.pending}
        readOnly={readOnly}
        message={section.message}
        errorFields={NO_FIELDS}
        errorRevision={section.revision}
        onSubmit={submit}
        onDiscard={section.discard}
      >
        {list.length === 0 ? (
          <p className={hintText}>
            No people yet. The order of this list is the order on the page.
          </p>
        ) : (
          <ol className={plainList} ref={focus.container}>
            {list.map((entry, index) => (
              <li
                key={`${entry.slug}:${entry.role}`}
                data-row-key={opaqueKey(`${entry.slug}:${entry.role}`)}
                className={chosenRow}
              >
                <span id={`person-name-${index}`} className={chosenName}>
                  {entry.displayName}
                </span>
                <NameWord id={`person-remove-${index}`}>Remove</NameWord>
                <div className="min-w-44 flex-1">
                  <SelectField
                    id={`person-role-${index}`}
                    label={`Role of ${entry.displayName}`}
                    value={entry.role}
                    options={personRoleOptions}
                    disabled={readOnly}
                    onChange={(role) =>
                      section.setValues((current) =>
                        current.map((other, otherIndex) =>
                          otherIndex === index ? { ...other, role } : other,
                        ),
                      )
                    }
                  />
                </div>
                {readOnly ? (
                  <span className={hintText}>
                    {personRoleLabels[entry.role as keyof typeof personRoleLabels]}
                  </span>
                ) : (
                  <>
                    <MoveButtons
                      nameId={`person-name-${index}`}
                      index={index}
                      count={list.length}
                      onMove={(delta) => move(index, delta)}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      aria-labelledby={`person-remove-${index} person-name-${index}`}
                      onClick={() => {
                        section.setValues((current) =>
                          current.filter((_, otherIndex) => otherIndex !== index),
                        );
                        live.announce(`Removed ${entry.displayName}.`);
                      }}
                    >
                      Remove
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ol>
        )}
        {readOnly ? null : (
          <LookupAdd
            label="Find a person"
            search={async (query, signal) =>
              (
                await api.searchPeople(
                  { limit: 10, ...(query === "" ? {} : { q: query }) },
                  { signal },
                )
              ).items
            }
            getKey={(item) => item.slug}
            getLabel={(item) => item.displayName}
            isChosen={() => false}
            onPick={(item) =>
              section.setValues((current) =>
                addPerson(current, {
                  slug: item.slug,
                  displayName: item.displayName,
                  role: "OTHER",
                }),
              )
            }
            disabled={command.pending}
            onCreate={() => setCreating(true)}
            createLabel="Create person"
          />
        )}
        <LiveRegion message={live.message} />
      </SectionForm>
      {creating ? (
        <CreateEntityDialog
          title="Create a person"
          fields={personFields}
          initial={{ slug: "", displayName: "", personKind: "INDIVIDUAL", shortBio: "" }}
          build={(values) => {
            const bio = (values.shortBio ?? "").trim();
            const body = {
              slug: (values.slug ?? "").trim(),
              displayName: (values.displayName ?? "").trim(),
              personKind: values.personKind ?? "",
              ...(bio === "" ? {} : { shortBio: bio }),
            };
            const checked = staffCatalogCreatePersonBodySchema.safeParse(body);
            return checked.success
              ? { ok: true, body: checked.data }
              : {
                  ok: false,
                  errors: firstErrors(checked.error.issues, {
                    slug: "",
                    displayName: "",
                    personKind: "",
                    shortBio: "",
                  }),
                };
          }}
          submit={async (body, idempotencyKey) =>
            void (await api.createPerson(body, { idempotencyKey }))
          }
          onCreated={(body) =>
            section.setValues((current) =>
              addPerson(current, { slug: body.slug, displayName: body.displayName, role: "OTHER" }),
            )
          }
          onClose={() => setCreating(false)}
        />
      ) : null}
    </section>
  );
}
